import { useSearchContext } from "@/lib/hooks/searchContext/searchStore";
import { useClientTranslation } from "@/app/i18n/client";
import { useSite } from "@/lib/hooks/site";
import { useTranslation } from "@/hooks/translationContext";
import { useEffect, useRef, useState } from "react";

function SearchHeader() {
  const { currentTab, setCurrentTab } = useSearchContext();
  const { t } = useTranslation()
  const site = useSite();

  // Long, site-specific venue tab title (e.g. "Voleybol Okulu Ara").
  const venueTitle = t("search-venue-tab-title", { defaultValue: "Mekan Adı" });
  // Short fallback used when the tabs overflow the page (e.g. "Okul Ara").
  const venueTitleShort = t("header-quicksearch-placeholder", {
    defaultValue: "Okul Ara",
  });

  const rootRef = useRef<HTMLDivElement>(null);
  const fullWidthRef = useRef(0);
  const [isCompact, setIsCompact] = useState(false);

  // When the tab row grows wider than the viewport it slides off the page
  // (both edges get clipped). In that case shorten the long venue tab title.
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const measure = () => {
      // While showing full titles the element's width is what we actually need.
      if (!isCompact) fullWidthRef.current = el.offsetWidth;
      if (!fullWidthRef.current) return;
      const available = window.innerWidth - 24;
      setIsCompact(fullWidthRef.current > available);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [isCompact, venueTitle, venueTitleShort, currentTab]);

  const tabs = [
    {
      tab: 0,
      title: t("header-search-district-service-tab-title"),
      isHidden: false,
    },
    {
      tab: 1,
      title: t("search-title-company"),
      isHidden: site?.setting?.disabledPages?.company === 1,
    },
    {
      tab: 2,
      title: isCompact ? venueTitleShort : venueTitle,
      isHidden: false,
    },
  ].filter((tab) => !tab.isHidden);

  const handleTabClick = (tab: number) => {
    setCurrentTab(tab);
  };

  const getActiveTabStyles = (tab: number) => {
    if (currentTab === tab) {
      return "border-b-2 border-rose-500";
    }

    return "";
  };

  const getTextStyles = (tab: number) => {
    if (currentTab === tab) {
      return "text-rose-500";
    }

    return "text-slate-800";
  };

  return (
      <div ref={rootRef} className="flex justify-center items-center gap-2">
        <div className="flex justify-center h-[26px] border-b gap-4 border-slate-300">
          {tabs.map((tab, index) => (
            <button
              role="button"
              key={index}
              onClick={() => handleTabClick(tab.tab)}
              className={`w-auto h-[26px] px-2 py-1 ${getActiveTabStyles(tab.tab)} flex-col justify-center items-center gap-1 inline-flex`}
            >
              <div
                className={`text-center text-sm font-bold font-['DM Sans'] ${getTextStyles(tab.tab)} whitespace-nowrap`}
              >
                {tab.title}
              </div>
            </button>
          ))}
        </div>
    </div>
  );
}

export default SearchHeader;
